import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import Select, { TOption } from "./Select";
import DialogButtons from "./DialogButtons";

const PLUS = "+";
const MINUS = "-";
const signOptions: TOption<string>[] = [{ value: PLUS }, { value: MINUS }];

const hoursValues = Array.from({ length: 24 }, (_, i) =>
  i.toString().padStart(2, "0")
);
const hoursOptions: TOption<string>[] = hoursValues.map(function (h) {
  return { value: String(h) };
});

const minutesValues = Array.from({ length: 6 }, (_, i) =>
  i.toString().padEnd(2, "0")
);
const minutesOptions: TOption<string>[] = minutesValues.map(function (m) {
  return { value: String(m) };
});

interface ITimeDialogProps {
  title: string;
  time: string;
  isSigned?: boolean;
  open: boolean;
  onClose: (time: string) => void;
}

const TimeDialog = (props: ITimeDialogProps) => {
  const { title, time, isSigned, open, onClose } = props;
  const [sign, setSign] = useState<string>("");
  const [hours, setHours] = useState<string>("00");
  const [minutes, setMinutes] = useState<string>("00");

  useEffect(() => {
    const [h, m] = time.split(":");
    if (isSigned) {
      setSign(h[0]);
      setHours(h.substring(1));
    } else {
      setHours(h);
    }
    setMinutes(m);
  }, [time, isSigned]);

  return (
    <Dialog open={open} data-cy="time-picker-time-dialog">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box gap={1} display="flex" flexDirection="row">
          {isSigned && (
            <Select
              label="+/-"
              value={sign}
              options={signOptions}
              onChange={(event) => {
                setSign(event.target.value);
              }}
              data-cy="time-picker-select-sign"
            />
          )}
          <Select
            label="hh"
            value={hours}
            options={hoursOptions}
            onChange={(event) => {
              setHours(event.target.value);
            }}
            data-cy="time-picker-select-hours"
          />
          <Select
            label="mm"
            value={minutes}
            options={minutesOptions}
            onChange={(event) => {
              setMinutes(event.target.value);
            }}
            data-cy="time-picker-select-minutes"
          />
        </Box>
      </DialogContent>
      <DialogButtons
        submitButtonLabel="Save"
        submitCallback={() => onClose(sign + hours + ":" + minutes)}
        resetCallback={() => onClose(time)}
      />
    </Dialog>
  );
};

type TTimePickerProps = {
  label: string;
  value?: string;
  isSigned?: boolean;
  onTimeChange: (value: string) => void;
};

const TimePicker = (props: TTimePickerProps & TextFieldProps) => {
  const { label, value, isSigned, onTimeChange, ...textFieldProps } = props;
  const [time, setTime] = useState<string>(value || "00:00");
  const [open, setOpen] = useState<boolean>(false);

  const handleClick = () => {
    setOpen(true);
  };

  const handleClose = (time: string) => {
    setOpen(false);
    setTime(time);
    onTimeChange(time);
  };

  return (
    <>
      <TextField
        {...textFieldProps}
        label={label}
        value={time}
        InputProps={{
          readOnly: true,
        }}
        onClick={handleClick}
      />
      <TimeDialog
        title={label}
        time={time}
        isSigned={isSigned}
        open={open}
        onClose={handleClose}
      />
    </>
  );
};

export default TimePicker;
